import { createContext, useContext } from 'react'
import { atom, type PrimitiveAtom } from 'jotai'
import { atomWithGlobalState } from '@highlight/ipc/jotai'
import type { ViewId } from './view-definitions'
import type { Claim } from './hooks/use-claim-atom'
import { createViewHistory, type ViewHistory, type ViewNavigationGuard } from './view-history'
import { isSidepanelOverlay } from './surface'

/**
 * The sidepanel view-router's atoms, alone in a module whose only runtime
 * imports are React, jotai and the pure `view-history` model — deliberately NOT
 * `view-atoms.ts`, whose hooks are recreated by hot updates reaching them.
 *
 * Same hot-reload hazard as `reminders-context.ts` / `visible-actions-context.ts`,
 * with atoms instead of contexts: a hot update reaching a module re-executes it,
 * and a re-executed atom is a NEW atom. Modules in the invalidated chain (the
 * router, which also imports the registry) would read the new one while mounted
 * cells from untouched modules still write the old — so clicking a cell updates
 * a history nobody renders. Atom identity lives here so no view edit can ever
 * recreate it; the hooks in `view-atoms.ts` may be recreated freely, since every
 * copy closes over these same atoms.
 */

/** Home is the root entry; it renders outside the router. */
export const HOME_VIEW_ID = 'home'

export interface SidepanelView {
  /**
   * A registry id, or Home's — the router renders the former and treats the
   * latter as "no view open". Type-only dependency on the registry, so this
   * stays free of the component graph it describes.
   */
  id: ViewId | typeof HOME_VIEW_ID
  context?: unknown
  sourceLabel?: string
  /** Skip the entrance animation when this view mounts. */
  skipEntrance?: boolean
}

export const createSidepanelViewHistory = (): ViewHistory<SidepanelView> => createViewHistory({ id: HOME_VIEW_ID })

/**
 * Only the overlay owns persisted sidepanel navigation. A popout window keeps
 * its own stack in this renderer, seeded with the popped View (see
 * `windows/view/view-window.tsx`), so it never observes the overlay's history.
 */
export const viewHistoryAtom = isSidepanelOverlay
  ? atomWithGlobalState<ViewHistory<SidepanelView>>('sidepanel.view.history', createSidepanelViewHistory(), {
      hydrateOnFirstRead: true,
    })
  : atom<ViewHistory<SidepanelView>>(createSidepanelViewHistory())

/** The mounted view's leave guard, if it holds unsent edits. */
export const viewNavigationGuardAtom = atom<ViewNavigationGuard | null>(null)

/**
 * A navigation the leave guard deferred while the view held unsaved edits —
 * non-null while the view's confirmation is open; calling it resumes the move.
 * (Set with `setPendingLeave(() => proceed)` — a bare function would be run as
 * a functional update.)
 */
export const pendingLeaveAtom = atom<(() => void) | null>(null)

/**
 * Jump-to-latest slot in the floating bar. A view body (e.g. the Meeting View
 * transcript) registers its scroll-to-bottom affordance here so the button
 * renders inline with the notepad and audio controls, on the same row and
 * baseline, instead of floating over content at its own guessed offset.
 */
export interface FloatingBarScrollSlot {
  show: boolean
  onClick: () => void
}

/**
 * Everything a mounted View shares with the chrome around it: the history it
 * navigates, its leave guard, and the floating bar, sheet layer and glow it
 * hands controls to. The panel and a popout window each have one. A View hosted
 * in a popover beside the panel gets its own from `createViewScope`, so it runs
 * unchanged there without touching the panel's navigation or chrome.
 */
/** A copy control the floating bar renders on a View's behalf. */
export interface FloatingBarCopy {
  /** Names what is copied, for the tooltip and the accessible name. */
  label: string
  copy: () => void
}

export interface ViewScope {
  /** Hosted outside the panel, beside it. */
  hosted: boolean
  history: PrimitiveAtom<ViewHistory<SidepanelView>>
  navigationGuard: PrimitiveAtom<ViewNavigationGuard | null>
  pendingLeave: PrimitiveAtom<(() => void) | null>
  /**
   * The floating controls sit above the footer outside its measured flow, so
   * scrollable surfaces reserve their height independently.
   */
  floatingBarHeight: PrimitiveAtom<number>
  /** The bottom-center wash tint the current View asked for; see `View.Glow`. */
  bottomGlow: PrimitiveAtom<Claim<string> | null>
  /**
   * What the floating bar's copy control copies right now, published by
   * whichever part of the View owns the content on screen. One control that
   * follows the tab, rather than a copy button per section and a fourth in the
   * bar that only ever meant the summary.
   */
  floatingBarCopy: PrimitiveAtom<Claim<FloatingBarCopy> | null>
  /**
   * The layer a View's bottom sheet portals into (`ViewSheetLayer`).
   *
   * The element itself rather than a flag, because that's what a portal target
   * has to be. It lives in an atom because the layer is a sibling of the view
   * router while the sheet is declared deep inside a View, with the router's
   * transformed, clipping subtree in between.
   */
  sheetLayer: PrimitiveAtom<HTMLElement | null>
  /** Held by whichever bottom sheet is currently open in the layer. */
  openSheet: PrimitiveAtom<Claim<true> | null>
  floatingBarScrollSlot: PrimitiveAtom<FloatingBarScrollSlot | null>
}

const createViewChromeAtoms = () => ({
  floatingBarHeight: atom<number>(0),
  bottomGlow: atom<Claim<string> | null>(null),
  floatingBarCopy: atom<Claim<FloatingBarCopy> | null>(null),
  sheetLayer: atom<HTMLElement | null>(null),
  openSheet: atom<Claim<true> | null>(null),
  floatingBarScrollSlot: atom<FloatingBarScrollSlot | null>(null),
})

const sidepanelViewScope: ViewScope = {
  hosted: false,
  history: viewHistoryAtom,
  navigationGuard: viewNavigationGuardAtom,
  pendingLeave: pendingLeaveAtom,
  ...createViewChromeAtoms(),
}

/**
 * Hosted Views start their entry keys far above the panel's, so per-entry
 * state (scroll, drafts) keyed on them never collides with a panel entry.
 */
const HOSTED_ENTRY_KEY_STRIDE = 1_000_000
let hostedScopeCount = 0

/** A fresh scope rooted on `view`, for a View hosted outside the panel. */
export function createViewScope(view: SidepanelView): ViewScope {
  hostedScopeCount += 1
  const rootKey = hostedScopeCount * HOSTED_ENTRY_KEY_STRIDE
  return {
    hosted: true,
    history: atom<ViewHistory<SidepanelView>>({ entries: [{ key: rootKey, view }], index: 0, nextKey: rootKey + 1 }),
    navigationGuard: atom<ViewNavigationGuard | null>(null),
    pendingLeave: atom<(() => void) | null>(null),
    ...createViewChromeAtoms(),
  }
}

export const ViewScopeContext = createContext<ViewScope>(sidepanelViewScope)

export const useViewScope = () => useContext(ViewScopeContext)

/**
 * Whether a View is visually present (including during its close animation).
 * Read by the main process's swipe-gesture handler to decide whether a back
 * swipe pops the View or dismisses the whole panel. That is the overlay's call
 * alone, so a popout writes a copy of its own and main never hears from it.
 * Same split as `viewHistoryAtom`, hydrated the same way.
 */
export const viewOpenAtom = isSidepanelOverlay
  ? atomWithGlobalState<boolean>('sidepanel.view.open', false, { hydrateOnFirstRead: true })
  : atom<boolean>(false)

/**
 * Per-history-entry view state (ADR 0001): scroll offsets, unsent input drafts,
 * and collapsed/expanded sections keyed by entry key, so back/forward restores
 * them after the view remounts. Everything else re-derives on remount.
 */
export const viewScrollPositionsAtom = atom<Record<number, number>>({})
export const viewDraftsAtom = atom<Record<number, string>>({})
/**
 * Only *explicit* disclosures land here — a view's own defaults are re-derived
 * on every render. Storing overrides rather than resolved values is what lets a
 * view's state advance (changing its defaults) without stomping a section the
 * user opened or closed by hand.
 */
export const viewSectionDisclosureAtom = atom<Record<number, Record<string, boolean>>>({})
/**
 * Which tab a tabbed view (the Meeting View) is on, per entry. Same rule as the
 * disclosures: only an explicit pick lands here, and the view's lifecycle
 * default is re-derived on every render.
 */
export const viewTabAtom = atom<Record<number, string>>({})
